import { Inject, Injectable, PLATFORM_ID } from "@angular/core";
import { isPlatformBrowser } from "@angular/common";
import { HttpInterceptor, HttpRequest, HttpHandler, HttpEvent, HttpResponse } from '@angular/common/http';
import { AuthService } from '../auth/services/auth.service';
import { Observable } from 'rxjs';
import { Router } from "@angular/router";
import { switchMap, tap } from 'rxjs/operators';
import { ConfirmdialogServiceService } from '../shared/confirmation-dialog/confirmdialog-service.service';
import { EncryptionService } from './encryption.service';
import { environment } from 'src/environments/environment';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
    private isErrorShowing = false;
    private publicKey: string = environment.publicKey;
    constructor(
        private authService: AuthService,
        private router: Router,
        private confirmationDialog: ConfirmdialogServiceService,
        private encryptionService: EncryptionService,
        @Inject(PLATFORM_ID) private platformId: Object
    ) { }

    private showError(message: string): void {
        if (this.isErrorShowing) return;
        this.isErrorShowing = true;
        this.confirmationDialog
            .showConfirmationDialog(message, 'Error', { ok: true })
            .subscribe({ complete: () => { this.isErrorShowing = false; } });
    }

    private btoaCompat(str: string): string {
        if (isPlatformBrowser(this.platformId)) return window.btoa(str);
        return Buffer.from(str, 'binary').toString('base64');
    }

    intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        if (!isPlatformBrowser(this.platformId)) {
            return next.handle(request);
        }

        const skipEncrypt = !request.body
            || request.body instanceof FormData
            || request.headers.get('X-Special-Param') === 'delete';

        if (skipEncrypt) {
            return this.dispatch(request, request.body, next);
        }

        const encryptedBody = this.encryptionService.encrypt(request.body, this.publicKey);
        return this.dispatch(request, encryptedBody, next);
    }

    private dispatch(request: HttpRequest<any>, body: any, next: HttpHandler): Observable<HttpEvent<any>> {
        const token = localStorage.getItem('token');
        const lang  = localStorage.getItem('lang');

        const cloned = token
            ? request.clone({
                headers: request.headers.delete('X-Special-Param'),
                body,
                setHeaders: {
                    Authorization: 'Basic ' + this.btoaCompat('admin:admin123'),
                    'x-access-token': token,
                    'lang': lang ?? '',
                },
            })
            : request.clone({
                body,
                setHeaders: {
                    Authorization: 'Basic ' + this.btoaCompat('admin:admin123'),
                },
            });

        const skipAutoLogout = request.url.includes('/auth/change-password');

        return next.handle(cloned).pipe(
            tap(
                event => {
                    if (event instanceof HttpResponse && !skipAutoLogout) {
                        if (event.body.status == 401 || event.body.status == 403) {
                            this.authService.logout();
                            this.router.navigate(['/sign-in']);
                        }
                    }
                },
                error => {
                    const msg = error?.error?.message || 'Some error happened on server. Please try again!!!..';
                    if (error.status == 500) {
                        if (error.error?.errorCode == 101) {
                            this.showError('You are already logged in with different device.');
                        } else {
                            this.showError(msg);
                        }
                    } else if (error.status == 401 || error.status == 403) {
                        this.authService.logout();
                        this.router.navigate(['/sign-in']);
                        this.showError(msg);
                    } else {
                        this.showError(msg);
                    }
                }
            )
        );
    }
}
